import { useEffect, useState } from "react";
import api from "../../lib/api";
import AdminLayout from "./AdminLayout";
import { toast, Toaster } from "sonner";
import { Inbox, Mail, MessageCircle, CheckCircle2, Trash2, Reply } from "lucide-react";
import { ddmmyyyy } from "../../lib/dates";

/**
 * Contact Us inbox — visible to all admins.
 * Lists messages submitted via the public /contact form.
 */
export default function AdminContactMessages() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("open"); // 'open' | 'resolved' | 'all'

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/contact-messages", { params: { limit: 500 } });
            setRows(data);
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const setStatus = async (id, status) => {
        try {
            await api.put(`/admin/contact-messages/${id}`, { status });
            setRows(prev => prev.map(r => r.id === id ? { ...r, status } : r));
            toast.success(status === "resolved" ? "Marked as resolved" : "Reopened");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this message? This cannot be undone.")) return;
        try {
            await api.delete(`/admin/contact-messages/${id}`);
            setRows(prev => prev.filter(r => r.id !== id));
            toast.success("Deleted");
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const visible = rows.filter(r => filter === "all" ? true : (r.status || "open") === filter);
    const openCount = rows.filter(r => (r.status || "open") === "open").length;

    return (
        <AdminLayout>
            <Toaster theme="dark" richColors position="top-center" />
            <div className="p-4 sm:p-8 max-w-4xl">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                        <Inbox className="w-5 h-5 text-[#00FF66]" />
                        <h1 className="font-display text-3xl font-bold uppercase text-white">Contact Inbox</h1>
                        {openCount > 0 && (
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] text-[10px] font-bold uppercase tracking-widest" data-testid="open-count-badge">
                                {openCount} open
                            </span>
                        )}
                    </div>
                    <div className="inline-flex bg-slate-900 border border-slate-800 rounded p-0.5 text-xs">
                        {["open", "resolved", "all"].map(f => (
                            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                                className={`px-3 h-9 rounded uppercase tracking-widest font-bold ${filter === f ? "bg-[#00FF66] text-black" : "text-slate-400 hover:text-white"}`}>
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
                <p className="text-slate-400 text-sm mt-1">Messages submitted via the public Contact Us form.</p>

                {loading ? (
                    <div className="mt-6 text-slate-500 text-sm">Loading…</div>
                ) : visible.length === 0 ? (
                    <div className="mt-6 bg-slate-900 border border-slate-800 rounded-lg p-12 text-center text-slate-500 text-sm">
                        No {filter === "all" ? "" : filter + " "}messages.
                    </div>
                ) : (
                    <div className="mt-6 space-y-3">
                        {visible.map(m => {
                            const status = m.status || "open";
                            const isResolved = status === "resolved";
                            return (
                                <div key={m.id} data-testid={`message-${m.id}`}
                                    className={`bg-slate-900 border rounded-lg p-4 ${isResolved ? "border-slate-800 opacity-75" : "border-slate-700"}`}>
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-white font-bold">{m.name}</span>
                                                {isResolved ? (
                                                    <span className="px-2 py-0.5 rounded-full bg-slate-800 text-slate-400 text-[10px] font-bold uppercase tracking-widest">Resolved</span>
                                                ) : (
                                                    <span className="px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40 text-[10px] font-bold uppercase tracking-widest">Open</span>
                                                )}
                                            </div>
                                            <a href={`mailto:${m.email}`} className="text-xs text-slate-400 hover:text-[#00FF66] inline-flex items-center gap-1">
                                                <Mail className="w-3 h-3" />{m.email}
                                            </a>
                                            <div className="text-[10px] text-slate-500 uppercase tracking-widest mt-0.5">{ddmmyyyy(m.created_at)}</div>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <a href={`mailto:${m.email}?subject=Re%3A%20Drive%20or%20Kick%20support`} title="Reply"
                                                data-testid={`reply-${m.id}`}
                                                className="inline-flex items-center justify-center w-9 h-9 rounded text-slate-400 hover:text-[#00FF66] hover:bg-slate-800">
                                                <Reply className="w-4 h-4" />
                                            </a>
                                            {!isResolved ? (
                                                <button onClick={() => setStatus(m.id, "resolved")} title="Mark resolved"
                                                    data-testid={`resolve-${m.id}`}
                                                    className="inline-flex items-center justify-center w-9 h-9 rounded text-slate-400 hover:text-[#00FF66] hover:bg-slate-800">
                                                    <CheckCircle2 className="w-4 h-4" />
                                                </button>
                                            ) : (
                                                <button onClick={() => setStatus(m.id, "open")} title="Reopen"
                                                    data-testid={`reopen-${m.id}`}
                                                    className="inline-flex items-center justify-center w-9 h-9 rounded text-slate-400 hover:text-amber-400 hover:bg-slate-800">
                                                    <MessageCircle className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button onClick={() => remove(m.id)} title="Delete"
                                                data-testid={`delete-${m.id}`}
                                                className="inline-flex items-center justify-center w-9 h-9 rounded text-slate-400 hover:text-red-400 hover:bg-slate-800">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mt-3 bg-slate-950 border border-slate-800 rounded px-3 py-2 text-sm text-slate-300 whitespace-pre-wrap"
                                        data-testid={`message-body-${m.id}`}>
                                        {m.message}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}
